/**
 * Isometric wall segment sprites.
 *
 * Uses WALL_PALETTE from palette.ts:
 *   0 = transparent
 *   1 = wall face NW
 *   2 = wall top / cap
 *   3 = wall shadow
 *   4 = wall edge highlight
 *   5 = wall face NE
 *   6 = wall NE shadow
 *   7 = wall base shadow
 *
 * Walls are 64x44: standard 64x32 tile width with 12px of wall height above.
 * Face width: 4px (clean, compact visual).
 * Horizontal panel lines every 4 rows for brick/panel detail.
 *
 * Each wall sprite traces ONE diagonal only (top-half of diamond edge).
 * When stacked per-tile along a building edge, they form a continuous straight line.
 */

import type { SpriteDefinition } from "../SpriteSheet.js";

const W = 64;
const WALL_HEIGHT = 12;
const H = 32 + WALL_HEIGHT; // 44

// Helpers
function px(pixels: number[], x: number, y: number, color: number): void {
  if (x >= 0 && x < W && y >= 0 && y < H) {
    pixels[y * W + x] = color;
  }
}

/** Draw a wall face strip at a given position. */
function drawWallStrip(
  pixels: number[],
  edgeX: number,
  wallTop: number,
  wallBottom: number,
  faceWidth: number,
  faceColor: number,
  shadowColor: number,
  direction: "right" | "left",
): void {
  for (let y = wallTop; y <= wallBottom; y++) {
    // Wall face
    for (let dx = 0; dx < faceWidth; dx++) {
      const x = direction === "right" ? edgeX + dx : edgeX - dx;
      px(pixels, x, y, faceColor);
    }

    // Cap (top row)
    if (y === wallTop) {
      for (let dx = 0; dx < faceWidth; dx++) {
        const x = direction === "right" ? edgeX + dx : edgeX - dx;
        px(pixels, x, y, 2);
      }
      px(pixels, edgeX, y, 4);
    }

    // Panel lines every 4 rows
    const rowInWall = y - wallTop;
    if (rowInWall > 1 && rowInWall % 4 === 0) {
      for (let dx = 0; dx < faceWidth; dx++) {
        const x = direction === "right" ? edgeX + dx : edgeX - dx;
        px(pixels, x, y, 2);
      }
    }

    // Shadow strip
    const shadowX = direction === "right" ? edgeX + faceWidth : edgeX - faceWidth;
    px(pixels, shadowX, y, shadowColor);
  }

  // Cap highlight
  if (direction === "right") {
    px(pixels, edgeX, wallTop, 4);
    px(pixels, edgeX + 1, wallTop, 4);
  } else {
    px(pixels, edgeX, wallTop, 4);
    px(pixels, edgeX - 1, wallTop, 4);
  }

  // Base shadow
  for (let dx = 1; dx < faceWidth + 1; dx++) {
    const x = direction === "right" ? edgeX + dx : edgeX - dx;
    px(pixels, x, wallBottom + 1, 7);
  }
}

/**
 * NW-facing wall — straight diagonal from top vertex toward left vertex.
 * Only traces the top-left edge (rows 0-15). When stacked per-tile,
 * consecutive segments form a continuous straight wall line.
 */
function makeWallNW(): number[] {
  const pixels = new Array<number>(W * H).fill(0);
  const halfH = 16;
  const faceWidth = 4;

  for (let row = 0; row < halfH; row++) {
    const edgeX = 32 - (row + 1) * 2;
    const wallTop = row;
    const wallBottom = row + WALL_HEIGHT;
    drawWallStrip(pixels, edgeX, wallTop, wallBottom, faceWidth, 1, 3, "right");
  }

  return pixels;
}

/**
 * NE-facing wall — straight diagonal from top vertex toward right vertex.
 * Only traces the top-right edge (rows 0-15). When stacked per-tile,
 * consecutive segments form a continuous straight wall line.
 */
function makeWallNE(): number[] {
  const pixels = new Array<number>(W * H).fill(0);
  const halfH = 16;
  const faceWidth = 4;

  for (let row = 0; row < halfH; row++) {
    const edgeX = 32 + (row + 1) * 2 - 1;
    const wallTop = row;
    const wallBottom = row + WALL_HEIGHT;
    drawWallStrip(pixels, edgeX, wallTop, wallBottom, faceWidth, 5, 6, "left");
  }

  return pixels;
}

/** NW-facing wall segment (top-left edge of tile). */
export const WALL_NW: SpriteDefinition = {
  name: "wall-nw",
  frames: [{ width: W, height: H, pixels: makeWallNW() }],
  originX: W / 2,
  originY: WALL_HEIGHT,
};

/** NE-facing wall segment (top-right edge of tile). */
export const WALL_NE: SpriteDefinition = {
  name: "wall-ne",
  frames: [{ width: W, height: H, pixels: makeWallNE() }],
  originX: W / 2,
  originY: WALL_HEIGHT,
};
